import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { useToast, Toast } from '../../components/ui/Toast';
import { friendlyAuthError } from '../../lib/authErrors';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha todos os campos.');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left decorative panel ── */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=800&q=80"
          alt="Wedding"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-rose-950/75" />

        {/* Floating decorations */}
        <div className="absolute top-8 right-8 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
        <div className="absolute bottom-20 left-8 w-24 h-24 bg-rose-300/20 rounded-full blur-xl" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/brand/icon.png" alt="" className="w-9 h-9 rounded-xl" />
            <span className="font-bold text-xl">Mimo do Casal</span>
          </Link>

          {/* Como funciona */}
          <div>
            <p className="text-3xl font-serif font-semibold leading-snug mb-8 text-white/95">
              Crie a página, compartilhe o link e receba os presentes por Pix ou cartão.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {['Sem mensalidade', '2,99% por presente', 'R$ 5 por saque'].map(s => (
                <span key={s} className="bg-white/15 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 text-sm text-white/80">
                  {s}
                </span>
              ))}
            </div>

            <ol className="space-y-3">
              {[
                'Crie a conta grátis, sem cartão',
                'Monte a lista e compartilhe o link',
                'O convidado paga por Pix ou cartão',
              ].map((step, i) => (
                <li key={step} className="flex items-center gap-3 bg-white/10 backdrop-blur border border-white/20 rounded-2xl px-4 py-3">
                  <span className="w-7 h-7 rounded-full bg-white/15 border border-white/30 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-white/90 text-sm">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-gradient-to-br from-rose-50/60 to-white">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/brand/icon.png" alt="" className="w-9 h-9 rounded-xl" />
              <span className="font-bold text-xl text-gray-900">Mimo do Casal</span>
            </Link>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Bem-vindo de volta 💕
            </h1>
            <p className="text-gray-500">Entre para gerenciar a página do seu casamento.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all hover:border-gray-300"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Senha</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full pl-10 pr-12 py-3.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all hover:border-gray-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Forgot password */}
            <div className="flex justify-end -mt-1">
              <Link
                to="/forgot-password"
                className="text-sm text-rose-500 hover:text-rose-600 font-medium transition-colors"
              >
                Esqueci minha senha
              </Link>
            </div>

            <Button type="submit" size="lg" className="w-full" loading={loading}>
              {!loading && (
                <>
                  <span>Entrar</span>
                  <ArrowRight size={18} />
                </>
              )}
              {loading && <span>Entrando...</span>}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-gradient-to-br from-rose-50/60 to-white px-4 text-sm text-gray-400">
                ou
              </span>
            </div>
          </div>

          {/* Sign up link */}
          <p className="text-center text-gray-500 text-sm">
            Não tem conta?{' '}
            <Link
              to="/signup"
              className="text-rose-500 font-semibold hover:text-rose-600 transition-colors"
            >
              Criar conta grátis
            </Link>
          </p>

          {/* Demo hint */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-100 rounded-xl">
            <p className="text-xs text-amber-700 text-center">
              <span className="font-semibold">Demo:</span> Crie uma conta em{' '}
              <Link to="/signup" className="underline">Criar conta</Link>{' '}
              para testar a plataforma.
            </p>
          </div>
        </motion.div>
      </div>

      <Toast toasts={toast.toasts} removeToast={toast.removeToast} />
    </div>
  );
}
